import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Search, ArrowUpRight } from "lucide-react";
import { useRole } from "../context/RoleContext";
import { getOwnerMessages } from "../lib/ownerPortal";
import { ConsultPill, relativeDate } from "../lib/ownerUi";

/* ──────────────────────────────────────────────────────────────
   Filter definitions — chips along the top of the index.
   ────────────────────────────────────────────────────────────── */
const FILTERS = [
  { id: "all",       label: "All" },
  { id: "needs",     label: "Needs your reply" },
  { id: "requested", label: "Requested" },
  { id: "active",    label: "In consultation" },
  { id: "activated", label: "Activated" },
  { id: "declined",  label: "Declined" },
];

/* "Needs your reply" = active thread where the architect sent the latest
   message OR the row has unread items for the owner. */
function needsReply(c, userId) {
  if (!c) return false;
  if (c.status === "Activated" || c.status === "Declined") return false;
  if ((c.unread || 0) > 0) return true;
  const last = c.latest_message;
  if (!last) return false;
  return last.sender_id && last.sender_id !== userId;
}

/* ──────────────────────────────────────────────────────────────
   Page
   ────────────────────────────────────────────────────────────── */
export default function OwnerMessages() {
  const { user } = useRole();
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!user?.id) return;
    let alive = true;
    getOwnerMessages(user.id).then((d) => alive && setRows(d.items || []));
    return () => { alive = false; };
  }, [user?.id]);

  /* ─── derived: counts, search, groups ─── */
  const counts = useMemo(() => {
    if (!rows) return { all: 0, needs: 0, requested: 0, active: 0, activated: 0, declined: 0 };
    const c = {
      all: rows.length,
      needs: rows.filter((r) => needsReply(r, user?.id)).length,
      requested: rows.filter((r) => r.status === "Consultation Requested").length,
      active: rows.filter((r) => r.status === "In Consultation").length,
      activated: rows.filter((r) => r.status === "Activated").length,
      declined: rows.filter((r) => r.status === "Declined").length,
    };
    return c;
  }, [rows, user?.id]);

  const firms = useMemo(() => {
    if (!rows) return 0;
    return new Set(rows.map((r) => r.architect?.firm).filter(Boolean)).size;
  }, [rows]);

  const visible = useMemo(() => {
    if (!rows) return [];
    let v = rows;
    if (filter === "needs") v = v.filter((r) => needsReply(r, user?.id));
    else if (filter === "requested") v = v.filter((r) => r.status === "Consultation Requested");
    else if (filter === "active") v = v.filter((r) => r.status === "In Consultation");
    else if (filter === "activated") v = v.filter((r) => r.status === "Activated");
    else if (filter === "declined") v = v.filter((r) => r.status === "Declined");
    const needle = q.trim().toLowerCase();
    if (needle) {
      v = v.filter((r) => {
        const hay = [
          r.architect?.firm,
          r.parcel?.address,
          r.design?.name,
          r.latest_message?.body,
        ].filter(Boolean).join(" ").toLowerCase();
        return hay.includes(needle);
      });
    }
    return v;
  }, [rows, filter, q, user?.id]);

  /* Split: needs-reply group sits above the main feed, but only when
     no filter or 'all' filter is active and there are matches. */
  const groupedNeeds = (filter === "all" && !q.trim())
    ? visible.filter((r) => needsReply(r, user?.id))
    : [];
  const groupedRest = (filter === "all" && !q.trim())
    ? visible.filter((r) => !needsReply(r, user?.id))
    : visible;

  return (
    <section className="animate-fade-in-up" data-testid="owner-messages">
      {/* ─── Editorial header ─── */}
      <header>
        <div className="flex items-center gap-3">
          <span aria-hidden style={{ display: "inline-block", width: 28, height: 2, background: "#B14E2F" }} />
          <span className="wn-label" style={{ color: "#8F3C23" }}>
            Owner / Builder · Inbox
          </span>
        </div>
        <h1
          className="mt-3"
          style={{
            fontFamily: '"Fraunces", Georgia, serif',
            fontSize: "clamp(28px, 4vw, 40px)",
            fontWeight: 500,
            color: "#1B1F4B",
            lineHeight: 1.1,
            letterSpacing: "-0.01em",
            margin: 0,
          }}
          data-testid="owner-messages-h1"
        >
          Conversations.
        </h1>
        <p
          className="mt-2"
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#6C6B85",
            fontSize: 16,
            lineHeight: 1.5,
            maxWidth: 720,
            margin: 0,
          }}
        >
          Every consultation thread you have running — freshest first, with the
          ones waiting on your reply pushed to the top.
        </p>

        {/* Stat strip */}
        {rows !== null && (
          <div
            className="mt-6 grid grid-cols-3"
            style={{
              gap: 0,
              borderTop: "1px solid #D9D8E4",
              borderBottom: "1px solid #D9D8E4",
            }}
            data-testid="owner-messages-stats"
          >
            <Stat label="Threads" value={counts.all} />
            <Stat
              label="Needs your reply"
              value={counts.needs}
              accent={counts.needs > 0}
            />
            <Stat label="Architects" value={firms} last />
          </div>
        )}
      </header>

      {/* ─── Filter chips + search ─── */}
      {rows !== null && rows.length > 0 && (
        <div
          className="mt-8 flex flex-wrap items-center"
          style={{ gap: 8 }}
          data-testid="owner-messages-filters"
        >
          {FILTERS.map((f) => {
            const n = counts[f.id];
            return (
              <FilterChip
                key={f.id}
                label={`${f.label}${n != null ? ` (${n})` : ""}`}
                active={filter === f.id}
                disabled={n === 0 && f.id !== "all"}
                onClick={() => setFilter(f.id)}
                testid={`owner-messages-filter-${f.id}`}
              />
            );
          })}
          <div
            className="ml-auto flex items-center"
            style={{
              gap: 8,
              padding: "6px 10px",
              border: "1px solid #D9D8E4",
              borderRadius: 999,
              background: "#FBFAFD",
              minWidth: 240,
            }}
          >
            <Search size={14} color="#6C6B85" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search firm, address, design…"
              data-testid="owner-messages-search"
              style={{
                flex: 1,
                background: "transparent",
                border: "none",
                outline: "none",
                fontSize: 13,
                fontFamily: '"Nunito Sans", sans-serif',
                color: "#272636",
              }}
            />
          </div>
        </div>
      )}

      {/* ─── Loading / empty ─── */}
      {rows === null && (
        <div className="mt-8" style={{ color: "#6C6B85", fontStyle: "italic" }}>
          Loading messages…
        </div>
      )}

      {rows && rows.length === 0 && (
        <EmptyState />
      )}

      {rows && rows.length > 0 && visible.length === 0 && (
        <div className="mt-8" data-testid="owner-messages-empty-filter">
          <div
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 500,
              fontSize: 20,
              color: "#1B1F4B",
              lineHeight: 1.2,
            }}
          >
            Nothing matches this filter.
          </div>
          <div
            className="mt-1"
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              color: "#6C6B85",
              fontSize: 14,
            }}
          >
            Clear the filter or search to see every thread.
          </div>
        </div>
      )}

      {/* ─── Needs your reply group (hero) ─── */}
      {groupedNeeds.length > 0 && (
        <Group
          marker="§ A"
          eyebrow="Needs your reply"
          title={
            groupedNeeds.length === 1
              ? "1 thread is waiting on you."
              : `${groupedNeeds.length} threads are waiting on you.`
          }
          dek="The architect wrote last — your reply unblocks the consultation."
          rows={groupedNeeds}
          userId={user?.id}
          testId="owner-messages-needs"
          tone="hero"
        />
      )}

      {/* ─── Everything else ─── */}
      {groupedRest.length > 0 && (
        <Group
          marker={groupedNeeds.length > 0 ? "§ B" : null}
          eyebrow={groupedNeeds.length > 0 ? "Everything else" : null}
          rows={groupedRest}
          userId={user?.id}
          testId="owner-messages-rest"
        />
      )}
    </section>
  );
}

/* ──────────────── Stat ──────────────── */
function Stat({ label, value, accent, last }) {
  return (
    <div
      style={{
        padding: "16px 18px",
        borderRight: last ? "none" : "1px solid #D9D8E4",
      }}
    >
      <div className="wn-label" style={{ color: "#6C6B85" }}>
        {label}
      </div>
      <div
        className="tabular-nums mt-1"
        style={{
          fontFamily: '"Nunito Sans", sans-serif',
          fontWeight: 800,
          fontSize: 26,
          color: accent ? "#8F3C23" : "#1B1F4B",
          letterSpacing: "-0.005em",
          lineHeight: 1.1,
        }}
      >
        {value}
      </div>
    </div>
  );
}

/* ──────────────── Filter chip ──────────────── */
function FilterChip({ label, active, disabled, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      data-testid={testid}
      disabled={disabled}
      style={{
        fontFamily: '"Nunito Sans", sans-serif',
        fontWeight: 700,
        fontSize: 12,
        letterSpacing: "0.04em",
        padding: "6px 12px",
        borderRadius: 999,
        border: active ? "1px solid #8F3C23" : "1px solid #D9D8E4",
        background: active ? "#8F3C23" : "transparent",
        color: active ? "#FBFAFD" : (disabled ? "#9AA0C0" : "#1B1F4B"),
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.55 : 1,
        transition: "background-color 160ms ease, border-color 160ms ease",
      }}
    >
      {label}
    </button>
  );
}

/* ──────────────── Group ──────────────── */
function Group({ marker, eyebrow, title, dek, rows, userId, testId, tone }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div className="mt-10" data-testid={testId}>
      {(marker || eyebrow) && (
        <div className="flex items-center gap-3">
          <span
            aria-hidden
            style={{
              display: "inline-block",
              width: 22,
              height: 2,
              background: tone === "hero" ? "#B14E2F" : "#9AA0C0",
            }}
          />
          <span
            className="wn-label"
            style={{ color: tone === "hero" ? "#8F3C23" : "#6C6B85" }}
          >
            {marker ? `${marker} · ` : ""}{eyebrow}
          </span>
        </div>
      )}
      {title && (
        <h2
          className="mt-2"
          style={{
            fontFamily: '"Fraunces", Georgia, serif',
            fontWeight: 500,
            fontSize: "clamp(20px, 2.2vw, 26px)",
            color: "#1B1F4B",
            lineHeight: 1.2,
            margin: 0,
          }}
        >
          {title}
        </h2>
      )}
      {dek && (
        <p
          className="mt-1"
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#6C6B85",
            fontSize: 14,
            lineHeight: 1.5,
            margin: 0,
            maxWidth: 620,
          }}
        >
          {dek}
        </p>
      )}
      <div
        className="mt-5"
        style={{
          borderTop: "1px solid #D9D8E4",
        }}
      >
        {rows.map((c) => (
          <ThreadRow
            key={c.id}
            c={c}
            userId={userId}
            hero={tone === "hero"}
          />
        ))}
      </div>
    </div>
  );
}

/* ──────────────── ThreadRow (hairline-bordered, no card box) ──────────────── */
function ThreadRow({ c, userId, hero }) {
  const reply = needsReply(c, userId);
  const last = c.latest_message;
  const tsRaw = last?.sent_at || c.last_activity_at || c.opened_at;
  const isIter = last?.kind === "iteration_request";
  const snippet = (last?.body || "").trim();

  return (
    <Link
      to={`/owner/consultations/${c.id}`}
      data-testid={`owner-messages-row-${c.id}`}
      className="wn-msg-row"
      style={{
        display: "block",
        borderBottom: "1px solid #D9D8E4",
        borderLeft: reply ? "3px solid #B14E2F" : "3px solid transparent",
        padding: "18px 16px 18px 18px",
        textDecoration: "none",
        color: "inherit",
        background: hero ? "#FFF9F2" : "transparent",
        transition: "background-color 200ms ease, border-left-color 200ms ease",
      }}
    >
      <div
        className="grid items-start"
        style={{
          gridTemplateColumns: "minmax(0, 1fr) auto",
          gap: 24,
        }}
      >
        <div style={{ minWidth: 0 }}>
          {/* Pill row */}
          <div
            className="flex items-center flex-wrap"
            style={{ gap: 10, marginBottom: 6 }}
          >
            <ConsultPill status={c.status} />
            {reply && (
              <span
                className="wn-label"
                style={{
                  color: "#8F3C23",
                  fontWeight: 800,
                  letterSpacing: "0.08em",
                }}
                data-testid={`owner-messages-needsreply-${c.id}`}
              >
                Your move
              </span>
            )}
          </div>

          {/* Firm + unread chip */}
          <div className="flex items-center" style={{ gap: 10 }}>
            <div
              style={{
                fontFamily: '"Fraunces", Georgia, serif',
                fontWeight: 500,
                fontSize: 20,
                color: "#1B1F4B",
                lineHeight: 1.2,
              }}
            >
              {c.architect?.firm || "—"}
            </div>
            {c.unread > 0 && (
              <span
                className="tabular-nums"
                data-testid={`owner-messages-unread-${c.id}`}
                style={{
                  minWidth: 20,
                  height: 20,
                  padding: "0 7px",
                  borderRadius: 10,
                  background: "#B14E2F",
                  color: "#FBFAFD",
                  fontSize: 11,
                  fontWeight: 800,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  letterSpacing: 0,
                }}
              >
                {c.unread}
              </span>
            )}
          </div>

          {/* Italic address line */}
          <div
            className="mt-1"
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              color: "#6C6B85",
              fontSize: 13.5,
              lineHeight: 1.45,
            }}
          >
            {c.parcel?.address || "—"} · {c.design?.name || "—"}
          </div>

          {/* Latest message snippet */}
          {snippet && (
            <div
              className="mt-2"
              style={{
                color: "#272636",
                fontSize: 13.5,
                lineHeight: 1.55,
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: 2,
                overflow: "hidden",
                fontWeight: c.unread > 0 ? 600 : 400,
              }}
            >
              {isIter && (
                <span
                  className="wn-label"
                  style={{
                    color: "#8F3C23",
                    marginRight: 8,
                    verticalAlign: "1px",
                  }}
                >
                  Iteration
                </span>
              )}
              {snippet}
            </div>
          )}
        </div>

        {/* Right column: relative date + chevron */}
        <div
          className="flex flex-col items-end"
          style={{ gap: 10, flexShrink: 0 }}
        >
          <div
            className="tabular-nums"
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              color: "#6C6B85",
              fontSize: 12.5,
              whiteSpace: "nowrap",
            }}
          >
            {relativeDate(tsRaw)}
          </div>
          <ChevronRight size={16} color="#9AA0C0" />
        </div>
      </div>
    </Link>
  );
}

/* ──────────────── Empty state ──────────────── */
function EmptyState() {
  return (
    <div
      className="mt-10"
      data-testid="owner-messages-empty"
      style={{
        border: "1px solid #D9D8E4",
        borderRadius: 4,
        backgroundColor: "#FBFAFD",
        padding: "28px 28px",
        display: "grid",
        gridTemplateColumns: "1fr auto",
        alignItems: "center",
        gap: 20,
      }}
    >
      <div>
        <div
          style={{
            fontFamily: '"Fraunces", Georgia, serif',
            fontWeight: 500,
            fontSize: 22,
            color: "#1B1F4B",
            lineHeight: 1.2,
          }}
        >
          No conversations yet.
        </div>
        <div
          className="mt-2"
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#6C6B85",
            fontSize: 14,
          }}
        >
          Request a consultation from any design — the thread will live here.
        </div>
      </div>
      <Link
        to="/marketplace"
        className="wn-btn wn-btn-accent inline-flex items-center"
        style={{ gap: 8 }}
      >
        Browse designs
        <ArrowUpRight size={14} />
      </Link>
    </div>
  );
}
